import React, { useEffect, useMemo, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import { useTenant } from '../../context/TenantContext';
import { useUser } from '../../context/UserContext';
import { useMembership } from '../../context/MembershipContext';
import { ROLES } from '../../platform/constants';
import { effectiveRole, isTenantAdminForTenant } from '../../platform/membershipPermissions';
import ArticleAdminControls from './ArticleAdminControls';
import {
  ARTICLE_ESSENTIAL_SECTION_TITLE,
  ARTICLE_SKUPINA_ESSENTIAL,
  articleToSkupinaValue,
  buildArticleGroupManagementRows,
  buildArticleListGroupsSavePayload,
  buildArticleSkupinaOptions,
  findDuplicateChatListGroupLabel,
  isDefaultArticleListGroupId,
  normalizeArticleRecord,
  slugifyGroupId,
  validateChatListGroupLabels,
} from '../../services/articleListGroups';
import {
  buildArticlesGroupedListQuery,
  deleteArticle,
  mapArticleFromFirestore,
} from '../../services/articlesService';
import { filterByActiveTenant } from '../../services/tenantContentFilter';
import { saveArticleListGroups } from '../../services/tenantsService';
import './ArticleGroupManagement.css';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';

function FixedGroupRow({
  label,
  order,
  description = '',
  descriptionEditable = false,
  onDescriptionChange,
}) {
  return (
    <div className="article-group-management-grid__row article-group-management-grid__row--fixed">
      <div className="article-group-management-grid__cell">
        <span className="article-group-management-grid__label">{label}</span>
        <span className="article-group-management-grid__badge">Sistemska</span>
      </div>
      <div className="article-group-management-grid__cell">
        {descriptionEditable ? (
          <Form.Control
            as="textarea"
            rows={2}
            size="sm"
            value={description}
            onChange={(event) => onDescriptionChange?.(event.target.value)}
            placeholder="Kratek opis skupine na seznamu"
            aria-label={`Opis skupine ${label}`}
          />
        ) : (
          <span className="article-group-management-grid__note">{description || '—'}</span>
        )}
      </div>
      <div className="article-group-management-grid__cell article-group-management-grid__cell--order">
        <Form.Control
          type="number"
          size="sm"
          value={order}
          disabled
          readOnly
          aria-label={`Vrstni red ${order}`}
        />
      </div>
      <div className="article-group-management-grid__cell article-group-management-grid__cell--actions" />
    </div>
  );
}

function ArticleGroupManagement() {
  const { tenantId, config, reloadConfig } = useTenant();
  const { user, loading: userLoading } = useUser();
  const { memberships } = useMembership();
  const confirm = useConfirm();
  const toast = useToast();
  const [editableGroups, setEditableGroups] = useState([]);
  const [essentialRow, setEssentialRow] = useState(null);
  const [archiveRow, setArchiveRow] = useState(null);
  const [newGroupLabel, setNewGroupLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [assignmentFilter, setAssignmentFilter] = useState('all');

  useEffect(() => {
    const rows = buildArticleGroupManagementRows(config);
    setEditableGroups(rows.editableGroups);
    setEssentialRow(rows.essentialRow);
    setArchiveRow(rows.archiveRow);
  }, [config]);

  useEffect(() => {
    if (userLoading || !user || !tenantId) {
      setArticles([]);
      setArticlesLoading(userLoading);
      return undefined;
    }

    setArticlesLoading(true);
    const listQuery = buildArticlesGroupedListQuery(user, tenantId, memberships);
    const unsubscribe = onSnapshot(listQuery, (querySnapshot) => {
      const items = filterByActiveTenant(
        querySnapshot.docs.map(mapArticleFromFirestore),
        tenantId,
      );
      setArticles(items);
      setArticlesLoading(false);
    }, (error) => {
      console.error('Error fetching articles for assignment:', error);
      setArticles([]);
      setArticlesLoading(false);
    });

    return () => unsubscribe();
  }, [userLoading, user, tenantId, memberships]);

  const archiveDisplayOrder = editableGroups.length + 1;

  const skupinaOptions = useMemo(
    () => buildArticleSkupinaOptions(config),
    [config],
  );

  const skupinaLabelByValue = useMemo(
    () => new Map(skupinaOptions.map((option) => [option.value, option.label])),
    [skupinaOptions],
  );

  const assignmentFilters = useMemo(() => {
    const normalizedArticles = articles.map(normalizeArticleRecord);
    const counts = new Map();

    normalizedArticles.forEach((article) => {
      const value = articleToSkupinaValue(article);
      counts.set(value, (counts.get(value) || 0) + 1);
    });

    const filters = [
      { value: 'all', label: 'Vsi', count: normalizedArticles.length },
      ...skupinaOptions.map((option) => ({
        value: option.value,
        label: option.label,
        count: counts.get(option.value) || 0,
      })),
    ];

    return filters.filter((filter) => filter.value === 'all' || filter.count > 0);
  }, [articles, skupinaOptions]);

  const sortedArticles = useMemo(() => {
    const orderMap = new Map(skupinaOptions.map((option, index) => [option.value, index]));

    return articles
      .map(normalizeArticleRecord)
      .sort((a, b) => {
        const aValue = articleToSkupinaValue(a);
        const bValue = articleToSkupinaValue(b);
        const orderDiff = (orderMap.get(aValue) ?? 999) - (orderMap.get(bValue) ?? 999);
        if (orderDiff !== 0) return orderDiff;
        return (a.title || '').localeCompare(b.title || '', 'sl');
      });
  }, [articles, skupinaOptions]);

  const visibleArticles = useMemo(() => {
    if (assignmentFilter === 'all') {
      return sortedArticles;
    }
    return sortedArticles.filter(
      (article) => articleToSkupinaValue(article) === assignmentFilter,
    );
  }, [sortedArticles, assignmentFilter]);

  const canDeleteArticle = (article) => {
    if (isTenantAdminForTenant(memberships, tenantId, user)) {
      return true;
    }
    const role = effectiveRole(memberships, tenantId, user);
    return role === ROLES.LOCAL_ADMIN && article.uid === user?.uid;
  };

  const handleArticleGroupingSaved = (articleId, grouping) => {
    setArticles((prev) =>
      prev.map((item) =>
        (item.id === articleId
          ? normalizeArticleRecord({ ...item, ...grouping })
          : item)),
    );
  };

  const handleDeleteArticle = async (article) => {
    const confirmed = await confirm({
      title: 'Izbriši obrazec',
      message: `Ali ste prepričani, da želite izbrisati obrazec «${article.title}»?`,
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteArticle(article);
      toast.deleted('Obrazec je bil izbrisan.');
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Obrazca ni bilo mogoče izbrisati. Poskusite znova.');
    }
  };

  const handleSaveGroups = async () => {
    if (!tenantId || !essentialRow || !archiveRow) return;

    const labelError = validateChatListGroupLabels(editableGroups);
    if (labelError) {
      toast.error(labelError);
      return;
    }

    const articleListGroups = buildArticleListGroupsSavePayload({
      editableGroups,
      archiveRow,
    });
    const essentialSectionSubtitle = String(essentialRow.description || '').trim();

    setSaving(true);
    try {
      await saveArticleListGroups(tenantId, articleListGroups, {
        essentialSectionSubtitle,
      });
      await reloadConfig();
      toast.success('Skupine obrazcev so bile shranjene.');
    } catch (error) {
      console.error('Error saving article list groups:', error);
      toast.error('Skupin ni bilo mogoče shraniti. Poskusite znova.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddGroup = () => {
    const label = newGroupLabel.trim();
    if (!label) {
      toast.error('Vnesite naziv skupine.');
      return;
    }

    const duplicate = findDuplicateChatListGroupLabel(
      [...editableGroups, ...(essentialRow ? [essentialRow] : []), ...(archiveRow ? [archiveRow] : [])],
      label,
    );
    if (duplicate) {
      toast.error(
        `Skupina «${label}» že obstaja (kot «${duplicate.label}»). Nazivi skupin morajo biti enolični.`,
      );
      return;
    }

    const baseId = slugifyGroupId(label) || 'skupina';
    const existingIds = new Set(editableGroups.map((group) => group.id));
    let nextId = baseId;
    let suffix = 2;
    while (existingIds.has(nextId)) {
      nextId = `${baseId}-${suffix}`;
      suffix += 1;
    }

    setEditableGroups((prev) => [
      ...prev,
      { id: nextId, label, order: prev.length + 1 },
    ]);
    setNewGroupLabel('');
  };

  const handleRemoveGroup = (groupId) => {
    if (isDefaultArticleListGroupId(groupId) && groupId !== 'other') {
      return;
    }
    if (groupId === 'other') {
      return;
    }
    setEditableGroups((prev) =>
      prev
        .filter((group) => group.id !== groupId)
        .map((group, index) => ({ ...group, order: index + 1 })),
    );
  };

  return (
    <div className="article-group-management-stack">
      <Card className="article-group-management platform-content-detail-card">
        <Card.Header className="article-group-management__header">
          <div>
            <h2 className="article-group-management__title">Skupine obrazcev</h2>
            <p className="article-group-management__subtitle">
              Uredite nazive in opise razdelkov. «{ARTICLE_ESSENTIAL_SECTION_TITLE}» in «Arhiv» sta fiksna.
            </p>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="article-group-management-panel">
            <div
              className="article-group-management-grid article-group-management-grid--head"
              role="row"
            >
              <div role="columnheader">Naziv skupine</div>
              <div role="columnheader">Opis skupine</div>
              <div role="columnheader">Vrstni red</div>
              <div role="columnheader">Akcije</div>
            </div>

            {essentialRow && (
              <FixedGroupRow
                label={essentialRow.label}
                order={essentialRow.order}
                description={essentialRow.description || ''}
                descriptionEditable
                onDescriptionChange={(nextDescription) => {
                  setEssentialRow((prev) => (
                    prev ? { ...prev, description: nextDescription } : prev
                  ));
                }}
              />
            )}

            {editableGroups.map((group) => (
              <div key={group.id} className="article-group-management-grid__row">
                <div className="article-group-management-grid__cell">
                  <Form.Control
                    type="text"
                    size="sm"
                    value={group.label}
                    onChange={(event) => {
                      const nextLabel = event.target.value;
                      setEditableGroups((prev) =>
                        prev.map((item) =>
                          (item.id === group.id
                            ? { ...item, label: nextLabel }
                            : item)),
                      );
                    }}
                  />
                </div>
                <div className="article-group-management-grid__cell">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    value={group.description || ''}
                    onChange={(event) => {
                      const nextDescription = event.target.value;
                      setEditableGroups((prev) =>
                        prev.map((item) =>
                          (item.id === group.id
                            ? { ...item, description: nextDescription }
                            : item)),
                      );
                    }}
                    placeholder="Kratek opis skupine na seznamu"
                    aria-label={`Opis skupine ${group.label}`}
                  />
                </div>
                <div className="article-group-management-grid__cell article-group-management-grid__cell--order">
                  <Form.Control
                    type="number"
                    size="sm"
                    min={1}
                    max={archiveDisplayOrder - 1}
                    value={group.order ?? 1}
                    onChange={(event) => {
                      const nextOrder = Number(event.target.value);
                      if (!Number.isFinite(nextOrder)) return;
                      setEditableGroups((prev) =>
                        prev.map((item) =>
                          (item.id === group.id
                            ? { ...item, order: nextOrder }
                            : item)),
                      );
                    }}
                  />
                </div>
                <div className="article-group-management-grid__cell article-group-management-grid__cell--actions">
                  {!isDefaultArticleListGroupId(group.id) && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveGroup(group.id)}
                    >
                      Izbriši
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {archiveRow && (
              <FixedGroupRow
                label={archiveRow.label}
                order={archiveDisplayOrder}
                description={archiveRow.description || ''}
                descriptionEditable
                onDescriptionChange={(nextDescription) => {
                  setArchiveRow((prev) => (
                    prev ? { ...prev, description: nextDescription } : prev
                  ));
                }}
              />
            )}
          </div>

          <div className="article-group-management-footer">
            <Form.Group className="article-group-management-footer__field">
              <Form.Label className="mb-1">Nova skupina</Form.Label>
              <Form.Control
                type="text"
                placeholder="npr. Službene poti"
                value={newGroupLabel}
                onChange={(event) => setNewGroupLabel(event.target.value)}
              />
            </Form.Group>
            <div className="article-group-management-footer__actions">
              <Button variant="outline-secondary" onClick={handleAddGroup} disabled={saving}>
                Dodaj skupino
              </Button>
              <Button variant="primary" onClick={handleSaveGroups} disabled={saving}>
                Shrani skupine
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="article-group-management platform-content-detail-card">
        <Card.Header className="article-group-management__header">
          <div>
            <h2 className="article-group-management__title">Dodelitev obrazcev</h2>
            <p className="article-group-management__subtitle">
              Izberite skupino za vsak obrazec. Za vrhnji razdelek izberite «{ARTICLE_ESSENTIAL_SECTION_TITLE}» (največ 3).
            </p>
          </div>
        </Card.Header>
        <Card.Body>
          {articlesLoading ? (
            <div className="article-group-management__loading">
              <Spinner animation="border" size="sm" />
            </div>
          ) : articles.length === 0 ? (
            <p className="article-group-management__empty">Ni obrazcev za dodelitev.</p>
          ) : (
            <>
              <div className="article-group-assignment-filters" role="tablist" aria-label="Filter obrazcev">
                {assignmentFilters.map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    role="tab"
                    aria-selected={assignmentFilter === filter.value}
                    className={[
                      'article-group-assignment-filters__pill',
                      assignmentFilter === filter.value && 'article-group-assignment-filters__pill--active',
                      filter.value === ARTICLE_SKUPINA_ESSENTIAL && 'article-group-assignment-filters__pill--essential',
                    ].filter(Boolean).join(' ')}
                    onClick={() => setAssignmentFilter(filter.value)}
                  >
                    <span>{filter.label}</span>
                    <span className="article-group-assignment-filters__count">{filter.count}</span>
                  </button>
                ))}
              </div>

              <div className="article-group-assignment-panel">
                <div
                  className="article-group-assignment-grid article-group-assignment-grid--head"
                  role="row"
                >
                  <div role="columnheader">Obrazec</div>
                  <div role="columnheader">Trenutna skupina</div>
                  <div role="columnheader">Spremeni</div>
                  <div role="columnheader" className="article-group-assignment-grid__actions-head">Akcije</div>
                </div>

                {visibleArticles.map((article) => {
                  const skupinaValue = articleToSkupinaValue(article);
                  const skupinaLabel = skupinaLabelByValue.get(skupinaValue) || skupinaValue;
                  const isEssential = skupinaValue === ARTICLE_SKUPINA_ESSENTIAL;

                  return (
                    <div
                      key={article.id}
                      className={[
                        'article-group-assignment-grid__row',
                        isEssential && 'article-group-assignment-grid__row--essential',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="article-group-assignment-grid__cell article-group-assignment-grid__cell--title">
                        <strong>{article.title}</strong>
                        {article.description && (
                          <div className="article-group-assignment-grid__description">
                            {article.description}
                          </div>
                        )}
                      </div>
                      <div className="article-group-assignment-grid__cell">
                        <span
                          className={[
                            'article-group-assignment-grid__group-badge',
                            isEssential && 'article-group-assignment-grid__group-badge--essential',
                          ].filter(Boolean).join(' ')}
                        >
                          {skupinaLabel}
                        </span>
                      </div>
                      <div className="article-group-assignment-grid__cell article-group-assignment-grid__cell--skupina">
                        <ArticleAdminControls
                          article={article}
                          tenantId={tenantId}
                          tenantConfig={config}
                          tableCell
                          onSaved={(grouping) => handleArticleGroupingSaved(article.id, grouping)}
                        />
                      </div>
                      <div className="article-group-assignment-grid__cell article-group-assignment-grid__cell--actions">
                        {canDeleteArticle(article) && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="article-group-assignment-grid__delete-btn"
                            onClick={() => handleDeleteArticle(article)}
                            aria-label={`Izbriši obrazec ${article.title}`}
                          >
                            <FaTrash aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default ArticleGroupManagement;
